/* eslint-disable react/destructuring-assignment */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';
import { Button, Input, Form } from 'antd';
import ReCAPTCHA from 'react-google-recaptcha';

import InfoView from 'components/common/InfoView';
import { resetPassword, forgotPassword } from 'redux/actions/Auth';
import logo from '../assets/images/kitegateway_icon_dark.png';
import Scrollbars from 'react-custom-scrollbars';

const FormItem = Form.Item;

const PasswordResetForm = (props) => {
  const dispatch = useDispatch();
  const userEmail = useSelector(({ auth }) => auth.forgotPasswordMail);
  const { message } = useSelector(({ auth }) => auth.passwordReset);
  const [captchaToken, setCaptchaToken] = useState('');
  const [count, setCount] = useState(60);
  const [readOnly, setReadOnly] = useState(true);

  const handleSubmitFailure = (errorInfo) => {
    console.error('Error: ', errorInfo);
  };

  const handleSubmit = (values) => {
    dispatch(resetPassword(values));
    // Reset token after 2 minutes, since thats when
    // the token expires
    setTimeout(() => {
      setCaptchaToken('');
    }, 120000);
  };

  useEffect(() => {
    if (message === 'Password reset successfully!') {
      props.history.push('/signin');
    }
  }, [message, props.history]);

  useEffect(() => {
    if (!userEmail) {
      props.history.push('/forgot-password');
    }
  }, [userEmail, props.history]);

  useEffect(() => {
    let start = Number(count);
    const end = Number(0);

    if (start === end) return;

    const incrementTime = 2000;
    const timer = setInterval(() => {
      start -= 2;
      setCount(start);
      if (start === end) clearInterval(timer);
    }, incrementTime);
  }, [count]);

  const handleRegenerateCode = () => {
    dispatch(forgotPassword({ email: userEmail }));
    setCount(60);
  };

  const enablePasswordField = () => {
    setTimeout(() => setReadOnly(false), 300);
  };

  const disableAutoFill = () => {
    setReadOnly(true);
    enablePasswordField();
  };

  const passwordChanged = (e) => {
    if (e.target.value.trim().length === 0) {
      disableAutoFill();
    }
  };

  return (
    <Scrollbars>
      <div className="gx-login-container">
        <div className="gx-login-content">
          <div className="gx-login-header">
            <img src={logo} alt="Kitegateway" style={{ width: '45%' }} title="Kitegateway" />
          </div>
          <div className="gx-mb-4">
            <h2 className="gx-text-primary">Reset Your Password</h2>
            <p>
              Please enter the
              {' '}
              <b>OTP code</b>
              {' '}
              sent to
              {' '}
              {userEmail}
              {' '}
              and a new password
            </p>
            <p>
              If you haven&apos;t got the code sent to your email,
              you can generate another code
              {(count !== 0) ? ` in ${count} seconds.` : ' now.'}
            </p>
            <div className="password-reset-code">
              <Button
                type="primary"
                onClick={() => handleRegenerateCode()}
                disabled={((count !== 0) || !userEmail)}
              >
                Resend Code
              </Button>
            </div>
          </div>
          <Form
            layout="vertical"
            name="password-reset-form"
            onFinish={handleSubmit}
            onFinishFailed={handleSubmitFailure}
            className="gx-signin-form gx-form-row0"
            autoComplete="off"
          >
            <FormItem
              rules={[
                { required: true, message: 'OTP is required!' },
              ]}
              label="OTP"
              name="token"
            >
              <Input.Password autoComplete="off" />
            </FormItem>
            <FormItem
              rules={[
                { required: true, message: 'This input is required!' },
              ]}
              label="New Password"
              name="password"
            >
              <Input onChange={passwordChanged} onContextMenu={(e) => e.preventDefault()} onClick={disableAutoFill} readOnly={readOnly} onFocus={enablePasswordField} type="password" placeholder="Password" autoComplete="off" />
            </FormItem>
            <FormItem
              rules={[
                { required: true, message: 'This input is required!' },
              ]}
              label="Confirm Password"
              name="rePassword"
            >
              <Input onChange={passwordChanged} onContextMenu={(e) => e.preventDefault()} onClick={disableAutoFill} readOnly={readOnly} onFocus={enablePasswordField} type="password" placeholder="Password" autoComplete="off" />
            </FormItem>
            <FormItem>
              <ReCAPTCHA
                sitekey={process.env.REACT_APP_GOOGLE_CAPTCHA_SITE_KEY}
                onChange={setCaptchaToken}
                onExpired={() => setCaptchaToken('')}
              />
              <Input autoComplete="username" hidden disabled value={userEmail} />
            </FormItem>
            <FormItem>
              <Button
                type="primary"
                htmlType={captchaToken ? 'submit' : 'button'}
                className={`gx-mb-0 ${captchaToken ? 'enabled' : 'disabled'
                  }`}
              >
                Submit
              </Button>
              <div style={{ display: 'flex', marginTop: '7px', float: 'right' }}>
                <Link to="/signin">
                  Sign In
                  {' '}
                  {'→'}
                </Link>
              </div>
            </FormItem>
          </Form>
        </div>
        <InfoView />
      </div>
    </Scrollbars>
  );
};

PasswordResetForm.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

export default PasswordResetForm;
